import { useMutation } from '@tanstack/react-query';
import DOMPurify from 'dompurify';
import { useDispatch } from 'react-redux';
import { endpoints } from '@rossum/api-client';
import { HttpError } from '@rossum/api-client/errors';
import { GenerateRejectionPayload } from '@rossum/api-client/internal';
import { api } from '../../../../../lib/apiClient';
import { sanitizeLinks } from '../../../../../lib/htmlSanitization';
import { throwError } from '../../../../../redux/modules/messages/actions';

DOMPurify.addHook('afterSanitizeAttributes', sanitizeLinks);

// The LLM may echo back content (e.g. error messages) that contains arbitrary
// HTML such as nginx error pages with <h1> tags. When this raw HTML is handed
// to `html-to-draftjs`, heading/structural tags get turned into real
// header/block elements, which then render with the browser's default
// (huge) heading styles inside the WYSIWYG editor.
// Aligned with `HTMLMessage.tsx` sanitization, with `p` and `em` added to
// preserve paragraph structure in the WYSIWYG editor (without `p`,
// `html-to-draftjs` would merge the whole body into a single block).
const ALLOWED_TAGS = [
  'a',
  'b',
  'br',
  'em',
  'i',
  'li',
  'ol',
  'p',
  'strong',
  'u',
  'ul',
];
const FORBID_ATTR = ['style', 'draggable'];

const sanitizeGeneratedEmailBody = (body: string) =>
  DOMPurify.sanitize(body, { ALLOWED_TAGS, FORBID_ATTR });

export const useGenerateRejectionEmail = (
  handleOnSuccess: (body: string) => void
) => {
  const dispatch = useDispatch();
  return useMutation({
    mutationFn: (payload: GenerateRejectionPayload) =>
      api.request(endpoints.internal.generateRejectionEmail(payload)),

    onError: (e: HttpError) => {
      // do not throw on throttled request error
      if (e.code !== 429) {
        dispatch(throwError('generateRejectionEmailError'));
      }
    },

    onSuccess: response =>
      handleOnSuccess(sanitizeGeneratedEmailBody(response.emailBody)),
  });
};
